/**
 * wayai list — list organizations and hubs
 *
 * Usage:
 *   wayai list                    # all orgs → hubs
 *   wayai list --org <id>         # filter to one org
 *   wayai list --json             # structured output for agents/scripts
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';

export function parseArgs(args: string[]): { orgId: string | undefined; json: boolean } {
  let orgId: string | undefined;
  let json = false;
  for (let i = 0; i < args.length; i++) {
    if (args[i] === '--org' && args[i + 1]) {
      orgId = args[++i];
    } else if (args[i] === '--json') {
      json = true;
    }
  }
  return { orgId, json };
}

export async function listCommand(args: string[]): Promise<void> {
  const { orgId, json } = parseArgs(args);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const { organizations } = await client.organizations();

  const orgsToList = orgId
    ? organizations.filter((o) => o.id === orgId)
    : organizations;

  if (orgsToList.length === 0) {
    if (json) {
      console.log(JSON.stringify({ organizations: [] }, null, 2));
    } else {
      console.log(orgId ? `Organization ${orgId} not found.` : 'No organizations found.');
    }
    return;
  }

  // Fetch hubs for all orgs in parallel
  const results = await Promise.all(
    orgsToList.map((org) => client.listHubs(org.id).then((r) => ({ org, hubs: r.hubs })))
  );

  if (json) {
    const payload = {
      organizations: results.map(({ org, hubs }) => ({
        organization_id: org.id,
        organization_name: org.name,
        hubs: hubs.map((h) => ({
          hub_id: h.hub_id,
          hub_name: h.hub_name,
          preview_label: h.preview_label ?? null,
        })),
      })),
    };
    console.log(JSON.stringify(payload, null, 2));
    return;
  }

  for (const { org, hubs } of results) {
    console.log(`\n${org.name} (${org.id})`);

    if (hubs.length === 0) {
      console.log('  (no hubs)');
      continue;
    }

    for (const hub of hubs) {
      const label = hub.preview_label ? ` (${hub.preview_label})` : '';
      console.log(`  ${hub.hub_name}${label}  ${hub.hub_id}`);
    }
  }
}
